import React, { useEffect, useRef, useState } from 'react';

/**
 * Champ texte avec suggestions, trois au plus, servies par une fonction
 * asynchrone — pour la marque et le modèle d'une annonce, alimentés par
 * `GET /api/vehicle-models`, la même route que l'app mobile à la publication.
 *
 * Trois, comme pour le choix du propriétaire : on ne parcourt pas un
 * catalogue, on tape « peu » et on veut Peugeot. La saisie libre reste
 * possible — la liste des modèles n'est pas exhaustive, elle grossit avec ce
 * que les propriétaires publient — donc rien n'est jamais imposé : la
 * suggestion complète, elle ne remplace pas.
 *
 * Même leçon que le sélecteur de propriétaire : la liste est dans le flux, pas
 * en surimpression, sinon le panneau à défilement de l'éditeur la rogne.
 */

export type Suggestion = {
  /** Valeur écrite dans le champ si choisie. */
  value: string;
  label: string;
  /** Ligne secondaire, grise (ex. la marque d'un modèle). */
  detail?: string;
  /** Effet secondaire du choix (ex. remplir la marque en choisissant un modèle). */
  onPick?: () => void;
};

type SuggestInputProps = {
  value: string;
  onChange: (value: string) => void;
  /** Suggestions pour le texte tapé. Peut ignorer le texte et renvoyer une liste fixe. */
  fetchSuggestions: (query: string) => Promise<Suggestion[]>;
  placeholder?: string;
  className?: string;
  /** Redemander les suggestions quand ceci change (le type de véhicule, la marque). */
  refreshKey?: string;
};

const MAX = 3;
const DEBOUNCE_MS = 180;

const SuggestInput: React.FC<SuggestInputProps> = ({ value, onChange, fetchSuggestions, placeholder, className, refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Numéro de la dernière demande : une réponse plus ancienne qui arriverait
  // après une plus récente (réseau capricieux) ne doit pas l'écraser.
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const results = await fetchSuggestions(value);
        if (seq !== requestSeq.current) return;
        // Ce qui est déjà tapé exactement n'est pas une suggestion.
        const filtered = results.filter((s) => s.value.toLowerCase() !== value.trim().toLowerCase());
        setItems(filtered.slice(0, MAX));
        setHighlight(0);
      } catch {
        if (seq === requestSeq.current) setItems([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, refreshKey]);

  useEffect(() => {
    if (open && listRef.current && items.length > 0) {
      listRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    const handle = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const choose = (s: Suggestion) => {
    onChange(s.value);
    s.onPick?.();
    setOpen(false);
  };

  const handleKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (open) { event.stopPropagation(); setOpen(false); }
      return;
    }
    if (!open || items.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => (h + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(items[highlight]);
    } else if (event.key === 'Tab') {
      // Tab complète avec la suggestion en surbrillance puis passe au champ
      // suivant : c'est le geste naturel « marque, tab, modèle ».
      choose(items[highlight]);
    }
  };

  return (
    <div ref={containerRef}>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={handleKey}
        autoComplete="off"
        className={className}
      />
      {open && (items.length > 0 || loading) && (
        <div ref={listRef} className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {items.map((s, index) => (
            <button
              key={`${s.value}-${s.detail ?? ''}`}
              type="button"
              // mousedown plutôt que click : le blur du champ (et la fermeture
              // par le clic hors champ) partirait avant le click.
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setHighlight(index)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                highlight === index ? 'bg-indigo-50 text-indigo-800' : 'text-gray-700'
              }`}
            >
              <span className="block truncate">{s.label}</span>
              {s.detail && <span className="block text-[11px] text-gray-400 truncate">{s.detail}</span>}
            </button>
          ))}
          {loading && items.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400">Recherche…</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestInput;
